<template>
  <AgGridVue
    id="activity-log"
    v-bind="gridOptions"
    @rowClicked="onRowClicked"
    class="ag-theme-alpine ag-theme-activity-log flex-1"
  />
</template>

<script setup>
import { readonly } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { ModuleRegistry } from 'ag-grid-community'
import { ServerSideRowModelModule } from 'ag-grid-enterprise'
import { useCursorPagination } from '@/Components/DataGrid/Composables/cursor-pagination'
import { useResetOnSort } from '@/Components/DataGrid/Composables/reset-on-sort'
import { useHttpRequest } from '@/Components/DataGrid/Composables/http-request'
import { useColumnStateManagement } from '@/Components/DataGrid/Composables/column-state'
import { useFilterStateManagement } from '@/Components/DataGrid/Composables/filter-state'
import ActivityStatusCell from '@/Components/DataGrid/Cells/ActivityStatusCell.vue'
import ActivityResourceTypeCell from '@/Components/DataGrid/Cells/ActivityResourceTypeCell.vue'
import ActivityInitiatorCell from '@/Components/DataGrid/Cells/ActivityInitiatorCell.vue'
import { formatDateTime } from '@/utils/datetime'
import { usePreFilters } from '@/Components/DataGrid/Composables/prefilters'
import { LOG_ENDPOINT } from '@/Components/ActivityLog/log-types'
import { useGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useGridApi } from '@/Components/DataGrid/grid-api'
import { usePage } from '@inertiajs/vue3'

const { context } = useGridState()
const page = usePage()

ModuleRegistry.registerModules([ServerSideRowModelModule])

const emit = defineEmits(['rowClicked'])

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  filters: {
    type: Object,
    default: () => {}
  },
  initialCursor: {
    type: String,
    default: null
  }
})

const { gridApi, createGridOptions } = useGridApi()
const gridApiReadOnly = readonly(gridApi)

const { cursorPagination } = useCursorPagination({
  gridApi: gridApiReadOnly,
  filters: props.filters,
  endpoint: LOG_ENDPOINT,
  initialCursor: props.initialCursor
})

const { resetOnSort } = useResetOnSort()

const { httpRequest } = useHttpRequest({
  endpoint: LOG_ENDPOINT,
  filters: props.filters
})

usePreFilters({
  gridApi: gridApiReadOnly,
  filters: props.filters
})

useColumnStateManagement({
  gridApi: gridApiReadOnly,
  name: props.name
})

useFilterStateManagement({
  gridApi: gridApiReadOnly
})

const serverSideDatasource = {
  async getRows(params) {
    const isResetting = resetOnSort.getRowsPreHandler(params)
    if (isResetting) return

    params = cursorPagination.getRowsPreHandler(params)

    await httpRequest.fetchData(params, (data) => {
      return cursorPagination.getRowsPostHandler(data)
    })
  }
}

const gridOptions = createGridOptions(cursorPagination.gridOptionsSetup(), {
  onGridReady: (params) => {
    gridApi.value = params.api
    context.gridApi = gridApi
  },
  getRowId: (params) => params.data.id,

  rowModelType: 'serverSide',
  rowSelection: 'single',
  suppressRowClickSelection: false,

  serverSideDatasource,

  columnDefs: [
    ...(props.filters.resource_type === null
      ? [
          {
            headerName: 'Resource',
            field: 'resource',
            cellRenderer: ActivityResourceTypeCell
          }
        ]
      : []),
    {
      headerName: 'ID',
      field: 'id',
      width: 100
    },
    {
      headerName: 'Date',
      field: 'created_at',
      valueFormatter: (cell) => formatDateTime(cell.value)
    },
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: ActivityStatusCell
    },
    {
      headerName: 'Title',
      field: 'description',
      flex: 1,
      minWidth: 200,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Source',
      field: 'source.display_name',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: page.props.auth.activity.sources
      }
    },
    {
      headerName: 'Initiator',
      field: 'user.name',
      cellRenderer: ActivityInitiatorCell
    }
  ],

  defaultColDef: {
    resizable: true,
    sortable: false,
    floatingFilter: true,
    suppressHeaderMenuButton: true
  },

  paginationPageSize: 50,
  cacheBlockSize: 50,

  sideBar: false
})

let selectedRowNode = null

context.deselectSelectedRow = () => {
  if (selectedRowNode) {
    selectedRowNode.setSelected(false)
  }
}

function onRowClicked(params) {
  selectedRowNode = params.node
  emit('rowClicked', params)
}
</script>
